import { useState, useEffect } from 'react';
import { IconButton, Badge, Tooltip } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatIcon from '@mui/icons-material/Chat';
import { useAuthentication, useFirebaseReady } from 'hooks';
import { API_ENDPOINTS } from 'constants';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { databasePromise } from 'configuration';

function useTotalUnread(userId, firebaseReady) {
  const [totalUnread, setTotalUnread] = useState(0);
  const [hasAny, setHasAny] = useState(false);

  useEffect(() => {
    if (!userId || !firebaseReady) return;

    let unsubscribe;
    databasePromise.then((db) => {
      const q = query(
        collection(db, 'conversations'),
        where('participantIds', 'array-contains', userId),
      );
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let total = 0;
          snapshot.docs.forEach((doc) => {
            total += doc.data().unreadCount?.[userId] ?? 0;
          });
          setTotalUnread(total);
          setHasAny(snapshot.docs.length > 0);
        },
        (err) => {
          // Silently ignore permission errors during auth propagation
          console.warn('NavbarChatButton subscription error:', err.code);
        },
      );
    });

    return () => unsubscribe?.();
  }, [userId, firebaseReady]);

  return { totalUnread, hasAny };
}

export function NavbarChatButton() {
  const { user } = useAuthentication();
  // BUG-3 FIX: wait for Firebase auth token before subscribing
  const firebaseReady = useFirebaseReady();
  const { totalUnread, hasAny } = useTotalUnread(user?.uid, firebaseReady);

  const handleClick = () => {
    window.open(API_ENDPOINTS.CHAT.NEW, '_blank', 'noopener,noreferrer');
  };

  const tooltip = totalUnread > 0
    ? `${totalUnread} unread message${totalUnread > 1 ? 's' : ''}`
    : hasAny
      ? 'Open messages'
      : 'Start a conversation';

  return (
    <Tooltip title={tooltip} arrow>
      <IconButton
        onClick={handleClick}
        disabled={!user}
        size="small"
        color={totalUnread > 0 ? 'primary' : 'default'}
      >
        <Badge badgeContent={totalUnread > 0 ? totalUnread : null} color="error" max={9}>
          {hasAny ? <ChatIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
